import { notify } from '@affine/component';
import {
  AuthContainer,
  AuthContent,
  AuthHeader,
} from '@affine/component/auth-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { useI18n } from '@affine/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { useSelfhostLoginVersionGuard } from '../hooks/affine/use-selfhost-login-version-guard';
import type { SignInState } from '.';
import { Back } from './back';
import { clerkFapiUrl, getCachedDevBrowserJwt } from './clerk-dev-browser';

const emailRegex =
  /^(?:(?:[^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|((?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function validateEmail(email: string) {
  return emailRegex.test(email);
}

export const SignInStep = ({
  state: _state,
  changeState,
  onSkip,
  onAuthenticated,
}: {
  state: SignInState;
  changeState: Dispatch<SetStateAction<SignInState>>;
  onSkip: () => void;
  onAuthenticated?: (status: AuthSessionStatus) => void;
}) => {
  const t = useI18n();
  const serverService = useServiceOptional(ServerService);
  const versionError = useSelfhostLoginVersionGuard(serverService?.server);
  const authService = useService(AuthService);
  const [isMutating, setIsMutating] = useState(false);
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);

  const loginStatus = useLiveData(authService.session.status$);
  useEffect(() => {
    if (loginStatus === 'authenticated') {
      notify.success({
        title: t['com.affine.auth.toast.title.signed-in'](),
        message: t['com.affine.auth.toast.message.signed-in'](),
      });
    }
    onAuthenticated?.(loginStatus);
  }, [loginStatus, onAuthenticated, t]);

  const onContinue = useAsyncCallback(async () => {
    if (!validateEmail(email)) {
      setIsValidEmail(false);
      return;
    }

    setIsValidEmail(true);
    setIsMutating(true);

    try {
      // Ask Clerk to send an email_code OTP to the typed address
      const signInUrl = await clerkFapiUrl('/v1/client/sign_ins');
      const res = await fetch(signInUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          identifier: email,
          strategy: 'email_code',
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errors = data?.errors as
          | Array<{ code: string; message: string }>
          | undefined;
        const errCode = errors?.[0]?.code ?? '';
        // Clerk returns "session_exists" when the user already has an active
        // session. In that case, skip OTP and go straight to OIDC.
        if (
          errCode === 'session_exists' ||
          errCode === 'identifier_already_signed_in'
        ) {
          const p = new URLSearchParams({ provider: 'OIDC' });
          const dbJwt = getCachedDevBrowserJwt();
          if (dbJwt) p.set('__clerk_db_jwt', dbJwt);
          window.location.href = `/oauth/login?${p.toString()}`;
          return;
        }
        throw new Error(errors?.[0]?.message ?? 'Falha ao enviar o código');
      }

      const signInId = (data as { response: { id: string } }).response.id;

      changeState(prev => ({
        ...prev,
        email,
        step: 'signInWithEmail',
        clerkSignInId: signInId,
      }));
    } catch (err: unknown) {
      console.error(err);
      notify.error({
        title: 'Falha ao enviar código',
        message: (err as Error).message,
      });
    }

    setIsMutating(false);
  }, [changeState, email]);

  if (versionError) {
    return (
      <AuthContainer>
        <AuthHeader
          title="Bem-vindo de volta!"
          subTitle="Olá! Por favor, insira seus dados de acesso."
        />
        <AuthContent>
          <div style={{ color: '#F9F9F9', fontSize: '14px' }}>
            {versionError}
          </div>
        </AuthContent>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer>
      <AuthHeader
        title="Bem-vindo de volta!"
        subTitle="Olá! Por favor, insira seus dados de acesso."
      />

      <AuthContent>
        {/* Email input */}
        <div style={{ marginBottom: '32px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 400,
              color: '#FFFFFF',
              marginBottom: '8px',
            }}
          >
            Email
          </label>
          <input
            type="email"
            placeholder="Seu email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setIsValidEmail(true);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') onContinue();
            }}
            style={{
              height: '48px',
              width: '100%',
              borderRadius: '8px',
              border: isValidEmail ? '1px solid #272727' : '1px solid #f87171',
              backgroundColor: '#171717',
              padding: '0 16px',
              fontSize: '14px',
              color: '#FFFFFF',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = 'rgba(202, 249, 1, 0.5)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = isValidEmail
                ? '#272727'
                : '#f87171';
            }}
          />
          {!isValidEmail && (
            <p
              style={{
                fontSize: '12px',
                color: '#f87171',
                marginTop: '4px',
              }}
            >
              {t['com.affine.auth.sign.email.error']()}
            </p>
          )}
        </div>

        {/* Primary action — checks user and transitions to OTP verification step */}
        <button
          onClick={onContinue}
          disabled={isMutating}
          style={{
            display: 'flex',
            height: '48px',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            backgroundColor: isMutating ? 'rgba(202,249,1,0.5)' : '#CAF901',
            border: 'none',
            fontSize: '16px',
            fontWeight: 400,
            color: '#242D00',
            cursor: isMutating ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s',
            marginBottom: '12px',
          }}
          onMouseEnter={e => {
            if (!isMutating)
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                'rgba(202,249,1,0.9)';
          }}
          onMouseLeave={e => {
            if (!isMutating)
              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                '#CAF901';
          }}
        >
          Enviar código
        </button>

        {/* Footer */}
        <p
          style={{
            fontSize: '12px',
            color: '#737373',
            textAlign: 'center',
            marginTop: '8px',
          }}
        >
          Não tem uma conta?{' '}
          <a
            href="#"
            style={{
              color: '#CAF901',
              fontWeight: 500,
              textDecoration: 'underline',
            }}
            onClick={e => {
              e.preventDefault();
              onSkip();
            }}
          >
            Criar conta
          </a>
        </p>
      </AuthContent>

      {/* Self-hosted back button (only shown in self-hosted mode) */}
      {versionError === null && <Back changeState={changeState} />}
    </AuthContainer>
  );
};
